/**
 * The kill switch every event passes on its way out of the browser.
 *
 * Deliberately a module with NO imports. `buildBeforeSend` in `sentry-config.ts`
 * reads it, `sentry-init.ts` imports that file, and `sentry-gate.ts` imports
 * `sentry-init.ts` — so putting this state in `sentry-gate.ts` would close the
 * cycle `gate -> init -> config -> gate`.
 *
 * It answers a DIFFERENT question from the one `sentry-gate.ts` answers at
 * startup. That one decides whether a client is created at all, once, before
 * the app renders. This one can change while the page is open, which is what
 * makes turning the LaunchDarkly flag off stop the stream in tabs that are
 * already running rather than only in the next session.
 *
 * The default is `true` because it is read by `beforeSend`, which only runs on a
 * client that was already created — and a client is only created when the gate
 * at startup said yes. A `false` default would mean every event is dropped for
 * any caller that never wires the flag up, i.e. silence that looks exactly like
 * a working integration with no errors in it.
 */
let deliveryEnabled = true;

/** Whether an event captured right now would be handed to the transport. */
export const isSentryDeliveryEnabled = (): boolean => deliveryEnabled;

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Subscribes to anything that changes whether a report would be delivered.
 *
 * Exists because the answer moves TWICE on an ordinary page, and a component
 * that reads it once at render sees neither move. The client itself appears
 * late — `sentry-gate.ts` waits for LaunchDarkly before calling `Sentry.init`,
 * i.e. after the app has rendered — and the kill switch can close afterwards,
 * at any moment. The nav's "Report a bug" button is gated on that answer, so
 * without this it would render in the state the very first frame happened to
 * have and stay there.
 *
 * Shaped for `useSyncExternalStore`: returns its own unsubscribe.
 */
export const subscribeToSentryReporting = (
  listener: Listener
): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Announces that a client was created, so readers re-ask.
 *
 * Called by `sentryInit` rather than by this module, which cannot see the
 * client: this file deliberately has no imports (see above), and asking
 * `Sentry.getClient()` here would give it the whole SDK.
 */
export const notifySentryReportingChanged = (): void => {
  listeners.forEach((listener) => listener());
};

/**
 * Opens or closes the switch.
 *
 * Closing it stops events and transactions at `beforeSend` /
 * `beforeSendTransaction`, which is a drop, not a queue: nothing is replayed if
 * it is opened again. That is the intended behaviour for a kill switch thrown
 * because Sentry is costing too much or drowning in one repeated error.
 *
 * Note what this canNOT do: re-opening it does not create a client that was
 * never initialised. A deploy whose flag was off at startup stays silent until
 * the page is reloaded.
 */
export const setSentryDeliveryEnabled = (enabled: boolean): void => {
  if (deliveryEnabled === enabled) return;

  deliveryEnabled = enabled;
  notifySentryReportingChanged();
};
